/**
 * Native in-app purchases via RevenueCat Capacitor SDK.
 *
 * On native platforms (iOS/Android), this initializes the RevenueCat SDK and
 * exposes methods for fetching offerings, purchasing packages, and restoring
 * purchases. On web, all methods are no-ops — web uses Stripe directly.
 *
 * RevenueCat unifies entitlements from both App Store and Stripe purchases,
 * so users get the same access regardless of how they subscribed.
 */
import { Capacitor } from '@capacitor/core';
import type { PurchasesPackage, PurchasesOffering } from '@revenuecat/purchases-capacitor';
import { logDebug, logError, logWarn } from '@/lib/log';
import { stripWholeDollarCents, type TrialDescriptor } from '@/lib/paidLaunch';

// Lazy-import the RevenueCat plugin only on native platforms to avoid
// bundling native-only code in the web build.
let Purchases: typeof import('@revenuecat/purchases-capacitor').Purchases | null = null;

const isNative = Capacitor.isNativePlatform();

/** Package info returned to UI for display and purchase */
export interface NativePackage {
  identifier: string;
  /** e.g. "$monthly" or "$annual" */
  packageType: string;
  /** Localized price string from the store, e.g. "$9.99" */
  priceString: string;
  /** Price amount in the store's currency */
  price: number;
  /** ISO 4217 currency code for `price`, e.g. "USD" — lets UI derive prices
   *  (like an annual plan's per-month equivalent) in the buyer's currency. */
  currencyCode: string;
  /** Product title from the store */
  title: string;
  /** Product description from the store */
  description: string;
  /**
   * The store product identifier (e.g. "spending_tools_monthly") — the key
   * intro-offer eligibility is resolved against (`checkIntroEligibility`).
   */
  productId: string;
  /**
   * Normalized free-trial descriptor if this PRODUCT carries one (App Store
   * intro offer / Play free phase), else null.
   *
   * This is product metadata, NOT a statement about the person in front of us:
   * an iOS `introPrice` is present even for an Apple ID that already used its
   * intro offer. Never render trial copy off this alone — pair it with
   * eligibility (`useTrialEligibility`), or we promise a free month and then
   * charge immediately.
   */
  trial: TrialDescriptor | null;
  /** The underlying RC package object (opaque — pass back to purchasePackage) */
  _rcPackage: unknown;
}

/** Offering containing available packages */
export interface NativeOffering {
  identifier: string;
  monthly: NativePackage | null;
  annual: NativePackage | null;
  allPackages: NativePackage[];
}

/** Purchase result */
export interface PurchaseResult {
  success: boolean;
  entitlements: string[];
  error?: string;
  userCancelled?: boolean;
  /**
   * Whether the store actually started a FREE TRIAL for this purchase, when we
   * can tell: read off the resulting CustomerInfo. `undefined` means unknown —
   * the watchdog path never sees a CustomerInfo, so it can't answer.
   *
   * Callers must treat only an explicit `false` as "we promised a trial and the
   * store didn't give one" (see SpendingToolsPaywall). Pre-purchase eligibility
   * and the store's actual decision can disagree — storefront coverage, sandbox
   * drift — and this is the signal that catches it after the fact.
   */
  trialApplied?: boolean;
  /**
   * The store refused the purchase because this store account ALREADY OWNS the
   * subscription (RC PRODUCT_ALREADY_PURCHASED / Play ITEM_ALREADY_OWNED). The
   * current app uid holds no entitlement — otherwise the paywall's
   * already-covered guard would have blocked the sell — so the likeliest truth
   * is a second account / new uid, and Restore (not a fresh purchase) is the
   * recovery. Callers decide what to do with it (see SpendingToolsPaywall).
   */
  alreadyOwned?: boolean;
  /**
   * Restore results only: whether the restored membership is set to renew.
   * `false` flags the cancelled-but-still-active sub — the user has access
   * until expiry but did NOT resubscribe, and callers should say so instead
   * of implying the membership continues. Judged over the store-billed
   * entitlements when any exist, else over all active ones (so callers can
   * still speak honestly about a web-billed membership).
   */
  willRenew?: boolean;
  /**
   * Restore results only: active entitlement ids billed through a device app
   * store (APP_STORE / MAC_APP_STORE / PLAY_STORE) — the only memberships a
   * native Restore can actually surface. Web (STRIPE) and PROMOTIONAL
   * entitlements ride along in `entitlements` whether or not the restore did
   * anything, so an empty list here means the restore changed nothing and
   * callers must not present it as a success (2026-07-29 TestFlight: restoring
   * over a cancelled Stripe sub reported success and implied the ends-soon
   * banner was wrong — it wasn't).
   */
  storeBilledEntitlements?: string[];
  /** Restore results only: RC store values behind the active entitlements (uppercase). */
  activeStores?: string[];
}

/**
 * RC store values billed through the device's app store. Only these are
 * restorable via the native Restore flow and manageable in the device's
 * subscription settings.
 */
export const DEVICE_STORES = ['APP_STORE', 'MAC_APP_STORE', 'PLAY_STORE'];

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

let initialized = false;

/**
 * Initialize the RevenueCat SDK. Must be called once after Firebase auth
 * resolves the user. No-op on web.
 */
export const initPurchases = async (firebaseUid: string): Promise<void> => {
  if (!isNative || initialized) return;

  try {
    const mod = await import('@revenuecat/purchases-capacitor');
    Purchases = mod.Purchases;
    const { STOREKIT_VERSION, LOG_LEVEL } = mod;

    // Non-production builds get DEBUG logging, set BEFORE configure so the
    // configuration and offering-resolution passes are captured.
    //
    // Why this is worth the noise: at the default INFO level the SDK is loud
    // when a PRODUCT is missing ("Product not found ... PRODUCT_NOT_FOUND")
    // but SILENT when it drops a package because the product's base plan
    // didn't match the one RevenueCat has configured. Those two failures look
    // identical from the app — the package is simply absent from the offering
    // — and on 2026-09-10 that cost hours chasing an Android monthly plan
    // that vanished with no diagnostic anywhere. DEBUG names the base plan it
    // could not match.
    //
    // Production stays at the SDK default: DEBUG logs purchase payloads and
    // subscriber state, which does not belong in a shipped build.
    if (import.meta.env.VITE_APP_ENV !== 'production') {
      try {
        await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
      } catch {
        // Never let logging config block SDK init.
      }
    }

    const platform = Capacitor.getPlatform();
    const apiKey = platform === 'ios'
      ? import.meta.env.VITE_ENOUGH_REVENUECAT_IOS_API_KEY
      : import.meta.env.VITE_ENOUGH_REVENUECAT_ANDROID_API_KEY;

    if (!apiKey) {
      logWarn('[Purchases] No RevenueCat API key configured for', platform);
      return;
    }

    await Purchases.configure({
      apiKey,
      appUserID: firebaseUid,
      // iOS: PIN StoreKit 2 rather than letting RevenueCat pick (`DEFAULT` can
      // resolve to StoreKit 1 silently, and nothing surfaces which one won).
      // This is what makes intro-offer eligibility trustworthy:
      // - SK2 → Apple's own `isEligibleForIntroOffer`, scoped to the APPLE ID
      //   and its subscription group. Correct regardless of who is signed in.
      // - SK1 → inferred from the receipt and THIS RevenueCat app user's
      //   history. A user who signs up fresh on a device whose Apple ID already
      //   burned the intro offer has no history under that new uid, so SK1
      //   answers "eligible", the paywall promises a free month, and Apple
      //   charges immediately. Observed 2026-07-21 on prod TestFlight; real
      //   users hit it via delete-and-reinstall, a second account, or a shared
      //   family device.
      // Requires the App Store Connect In-App Purchase key in the RevenueCat
      // dashboard (uploaded, valid). SK2 needs iOS 16+; the SDK falls back to
      // SK1 on older versions on its own. Ignored on Android.
      ...(platform === 'ios' ? { storeKitVersion: STOREKIT_VERSION.STOREKIT_2 } : {}),
    });

    initialized = true;
    logDebug('[Purchases] RevenueCat SDK initialized for', platform);
  } catch (err) {
    logError(err, { context: 'purchases_initialize' });
  }
};

/**
 * Update the RevenueCat app user ID (e.g. on login).
 * If the SDK isn't initialized yet, this is a no-op.
 */
export const loginPurchases = async (firebaseUid: string): Promise<void> => {
  if (!isNative || !Purchases) return;

  try {
    await Purchases.logIn({ appUserID: firebaseUid });
  } catch (err) {
    logError(err, { context: 'purchases_login' });
  }
};

/**
 * Log out the current RevenueCat user (e.g. on logout).
 */
export const logoutPurchases = async (): Promise<void> => {
  if (!isNative || !Purchases) return;

  try {
    await Purchases.logOut();
  } catch (err) {
    logError(err, { context: 'purchases_logout' });
  }
};

// ---------------------------------------------------------------------------
// Offerings
// ---------------------------------------------------------------------------

/**
 * Normalize a store free trial into a TrialDescriptor. Handles both the iOS
 * intro-offer shape (`product.introPrice` with price 0) and the Android free
 * phase (`product.defaultOption.freePhase` / a zero-price pricing phase).
 * Returns null when there's no free phase. Typed loosely because the two
 * stores expose different shapes and the Capacitor SDK surfaces both.
 */
const extractTrial = (product: unknown): TrialDescriptor | null => {
  const p = product as {
    introPrice?: { price?: number; periodUnit?: string; periodNumberOfUnits?: number; period?: string } | null;
    defaultOption?: { freePhase?: { billingPeriod?: { unit?: string; value?: number } } | null } | null;
  };

  // iOS intro offer with a $0 price === a free trial.
  const intro = p.introPrice;
  if (intro && (intro.price ?? -1) === 0) {
    return periodToTrial(intro.periodUnit, intro.periodNumberOfUnits);
  }

  // Android free phase.
  const freePhase = p.defaultOption?.freePhase?.billingPeriod;
  if (freePhase) {
    return periodToTrial(freePhase.unit, freePhase.value);
  }

  return null;
};

/** Turn a store period (unit + count) into a human label + approx days. */
const periodToTrial = (unit: string | undefined, count: number | undefined): TrialDescriptor | null => {
  if (!unit || !count || count <= 0) return null;
  const u = unit.toUpperCase();
  const perUnitDays = u.startsWith('DAY') ? 1 : u.startsWith('WEEK') ? 7 : u.startsWith('MONTH') ? 30 : u.startsWith('YEAR') ? 365 : null;
  const noun = u.startsWith('DAY') ? 'day' : u.startsWith('WEEK') ? 'week' : u.startsWith('MONTH') ? 'month' : u.startsWith('YEAR') ? 'year' : null;
  if (perUnitDays == null || noun == null) return null;
  return {
    // Copy policy (see TrialDescriptor in lib/paidLaunch.ts): native trial
    // length copy is store-derived — Apple/Play month offers render "1 month",
    // matching Apple's own payment sheet. Never override this to "30 days".
    periodLabel: `${count} ${noun}${count === 1 ? '' : 's'}`,
    approxDays: perUnitDays * count,
    // Unit + count let trialEndDate() do calendar-exact billing-date math
    // (Apple's "1 month" is 28–31 days, renewing same day next month).
    unit: noun,
    count,
  };
};

const mapPackage = (pkg: PurchasesPackage): NativePackage => ({
  identifier: pkg.identifier,
  packageType: pkg.packageType,
  // Stores render whole dollars with cents ("$15.00"); our copy policy drops
  // them. Normalized here so every surface showing a package price inherits it.
  priceString: stripWholeDollarCents(pkg.product?.priceString ?? ''),
  price: pkg.product?.price ?? 0,
  currencyCode: pkg.product?.currencyCode ?? 'USD',
  title: pkg.product?.title ?? '',
  description: pkg.product?.description ?? '',
  productId: pkg.product?.identifier ?? '',
  trial: extractTrial(pkg.product),
  _rcPackage: pkg,
});

/**
 * Fetch the current offering from RevenueCat. Returns null on web or if
 * no offering is configured.
 */
export const getOfferings = async (): Promise<NativeOffering | null> => {
  if (!isNative || !Purchases) return null;

  try {
    const result = await Purchases.getOfferings();
    // Older SDK response shapes nested the offering under `.offerings.current`;
    // fall back to that for defensive compatibility.
    const legacyCurrent = (result as { offerings?: { current?: PurchasesOffering } })?.offerings?.current;
    const current = result?.current ?? legacyCurrent;

    if (!current) {
      logWarn('[Purchases] No current offering configured');
      return null;
    }

    const monthly = current.monthly ? mapPackage(current.monthly) : null;
    const annual = current.annual ? mapPackage(current.annual) : null;

    return {
      identifier: current.identifier,
      monthly,
      annual,
      allPackages: (current.availablePackages ?? []).map(mapPackage),
    };
  } catch (err) {
    logError(err, { context: 'purchases_fetch_offerings' });
    return null;
  }
};

/**
 * Resolve a package from an offering by type.
 *
 * `offering.monthly` / `offering.annual` are convenience accessors the SDK
 * derives; `availablePackages` is the authoritative list. On 2026-09-10 the
 * two disagreed on a dev Android build: RevenueCat served both `$rc_monthly`
 * and `$rc_annual` (verified against its API, correct base plans), Play
 * returned both subscriptions with valid offers, the SDK logged "Building
 * offerings response with 2 products" — and `offering.monthly` still arrived
 * null while `offering.annual` did not. The monthly plan silently vanished
 * from the paywall with no error anywhere.
 *
 * So trust the list first and treat the accessor as the fallback, matching on
 * the reserved identifier as well as packageType because a package configured
 * with a custom identifier reports PACKAGE_TYPE.CUSTOM.
 */
const pickPackage = (
  offering: { availablePackages?: unknown[] } & Record<string, unknown>,
  type: 'MONTHLY' | 'ANNUAL',
): unknown => {
  const reservedId = type === 'MONTHLY' ? '$rc_monthly' : '$rc_annual';
  const list = (offering.availablePackages ?? []) as Array<Record<string, unknown>>;
  const fromList = list.find(
    (p) => p?.packageType === type || p?.identifier === reservedId,
  );
  if (fromList) return fromList;
  return (type === 'MONTHLY' ? offering.monthly : offering.annual) ?? null;
};

/**
 * Fetch a SPECIFIC offering by identifier (e.g. the paid-launch `spending
 * tools` offering), rather than whatever is marked Current. This is what keeps
 * the paid-launch packages out of the app until we flip the flag: `default`
 * stays Current, and only the flag-gated paywall reads this named offering.
 * Returns null on web, on error, or when the offering isn't configured.
 */
export const getOfferingByIdentifier = async (identifier: string): Promise<NativeOffering | null> => {
  if (!isNative || !Purchases) return null;

  try {
    const result = await Purchases.getOfferings();
    const offering = result?.all?.[identifier];
    if (!offering) {
      logWarn('[Purchases] No offering configured for identifier', identifier);
      return null;
    }

    const monthlyPkg = pickPackage(offering as never, 'MONTHLY') as Parameters<typeof mapPackage>[0] | null;
    const annualPkg = pickPackage(offering as never, 'ANNUAL') as Parameters<typeof mapPackage>[0] | null;

    // Dev/test builds only: when a plan goes missing this is the ONLY place
    // that can say whether the SDK dropped the package or merely failed to
    // surface it on the convenience accessor.
    if (import.meta.env.VITE_APP_ENV !== 'production') {
      logWarn(
        '[Purchases] offering', identifier,
        '| packages:', (offering.availablePackages ?? []).map((p) => `${p.identifier}/${p.packageType}`).join(', ') || '(none)',
        '| accessor monthly:', offering.monthly ? 'present' : 'NULL',
        '| accessor annual:', offering.annual ? 'present' : 'NULL',
        '| resolved monthly:', monthlyPkg ? 'present' : 'NULL',
        '| resolved annual:', annualPkg ? 'present' : 'NULL',
      );
    }

    return {
      identifier: offering.identifier,
      monthly: monthlyPkg ? mapPackage(monthlyPkg) : null,
      annual: annualPkg ? mapPackage(annualPkg) : null,
      allPackages: (offering.availablePackages ?? []).map(mapPackage),
    };
  } catch (err) {
    logError(err, { context: 'purchases_fetch_offering_by_id' });
    return null;
  }
};

// ---------------------------------------------------------------------------
// Purchasing
// ---------------------------------------------------------------------------

/** Active entitlement ids from a CustomerInfo-shaped object. */
const activeIdsOf = (customerInfo: unknown): string[] => {
  const active = (customerInfo as { entitlements?: { active?: Record<string, { isActive?: boolean }> } })
    ?.entitlements?.active ?? {};
  return Object.entries(active)
    .filter(([, ent]) => ent.isActive)
    .map(([id]) => id);
};

/**
 * True when one of the active entitlements is currently in a FREE TRIAL.
 * RevenueCat reports an entitlement's period type as NORMAL / INTRO / TRIAL /
 * PREPAID; a $0 App Store introductory offer and a Play free phase both surface
 * as TRIAL (a PAID intro offer would be INTRO, which is not a free trial).
 */
const trialAppliedIn = (customerInfo: unknown): boolean => {
  const active = (customerInfo as {
    entitlements?: { active?: Record<string, { isActive?: boolean; periodType?: string }> };
  })?.entitlements?.active ?? {};
  return Object.values(active).some(
    (ent) => ent.isActive && String(ent.periodType ?? '').toUpperCase() === 'TRIAL',
  );
};

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// Purchase watchdog timing: leave the payment sheet alone at first, then check
// every few seconds, and go quiet after ~5 minutes (a user may legitimately sit
// on the sheet longer — the SDK promise stays the only signal after the cap).
const WATCHDOG_INITIAL_DELAY_MS = 8_000;
const WATCHDOG_POLL_INTERVAL_MS = 5_000;
const WATCHDOG_MAX_POLLS = 60;

/**
 * Purchase a package. Pass the NativePackage returned from getOfferings().
 * Returns the result with granted entitlements.
 *
 * Runs a WATCHDOG alongside the SDK call: the Capacitor bridge can lose the
 * `purchasePackage` callback — the store transaction completes (Apple even
 * shows its own "You're all set" alert) but the promise never settles, which
 * strands every caller's spinner forever. The native SDK still finishes the
 * purchase and updates its CustomerInfo, so while the SDK promise is pending
 * we poll for a NEWLY active entitlement (one not active before the purchase)
 * and treat its appearance as success. While the payment sheet is merely open,
 * no new entitlement exists and the watchdog stays silent.
 */
export const purchasePackage = async (pkg: NativePackage): Promise<PurchaseResult> => {
  if (!isNative || !Purchases) {
    return { success: false, entitlements: [], error: 'Purchases not available' };
  }

  // Snapshot BEFORE purchasing so the watchdog can tell a newly granted
  // entitlement apart from one the user already had. Best-effort: an empty
  // baseline just makes the watchdog treat any active entitlement as new.
  let entitlementsBefore: string[] = [];
  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    entitlementsBefore = activeIdsOf(customerInfo);
  } catch { /* keep the empty baseline */ }

  let settled = false;

  const sdkPurchase = (async (): Promise<PurchaseResult> => {
    try {
      const { customerInfo } = await Purchases!.purchasePackage({
        aPackage: pkg._rcPackage as PurchasesPackage,
      });
      return {
        success: true,
        entitlements: activeIdsOf(customerInfo),
        trialApplied: trialAppliedIn(customerInfo),
      };
    } catch (err: any) {
      // RevenueCat returns userCancelled flag when user dismisses the purchase sheet
      if (err?.userCancelled || err?.code === 1) {
        return { success: false, entitlements: [], userCancelled: true };
      }
      // Store refused because the user already owns this subscription (RC
      // PRODUCT_ALREADY_PURCHASED, code 6 — Play's ITEM_ALREADY_OWNED, or an
      // owned sub on a second app account). Detection is belt-and-suspenders:
      // the plugin has surfaced `code` in more than one shape, and RC's own
      // message for code 6 is "This product is already active for the user."
      // (seen raw on-device 2026-07-21 — "already active" was not matched).
      // Flagged `alreadyOwned` so the paywall can recover via restore instead
      // of stranding the user on an error they can't purchase past.
      if (
        String(err?.code ?? '') === '6'
        || /already\s+(own|purchas|subscrib|active)/i.test(String(err?.message ?? ''))
      ) {
        logWarn('[Purchases] purchase refused: subscription already owned by this store account');
        return {
          success: false,
          entitlements: [],
          alreadyOwned: true,
          error: "You already have this membership. To resubscribe, renew it from your device's subscription settings.",
        };
      }
      logError(err, { context: 'purchases_purchase' });
      return { success: false, entitlements: [], error: err?.message ?? 'Purchase failed' };
    }
  })();

  const watchdog = (async (): Promise<PurchaseResult> => {
    await sleep(WATCHDOG_INITIAL_DELAY_MS);
    for (let i = 0; i < WATCHDOG_MAX_POLLS && !settled; i++) {
      try {
        const { customerInfo } = await Purchases!.getCustomerInfo();
        const active = activeIdsOf(customerInfo);
        if (active.some((id) => !entitlementsBefore.includes(id))) {
          logWarn('[Purchases] purchase completed but the SDK promise never settled — resolved via watchdog');
          return { success: true, entitlements: active };
        }
      } catch { /* transient fetch failure — keep watching */ }
      await sleep(WATCHDOG_POLL_INTERVAL_MS);
    }
    // Cap reached (or the SDK settled the race): go quiet forever so the SDK
    // promise is the only remaining signal — i.e. pre-watchdog behavior.
    return new Promise<never>(() => {});
  })();

  const result = await Promise.race([sdkPurchase, watchdog]);
  settled = true;
  return result;
};

/**
 * Ask Apple whether THIS Apple ID may still redeem the introductory offer
 * ("Free for the first month") on each of `productIds`, returning a map of
 * productId -> may-we-promise-a-trial.
 *
 * Why this exists: `extractTrial` reads `product.introPrice`, which is static
 * product METADATA — it is present whether or not the person in front of us is
 * eligible. Rendering trial copy off it alone would promise a free month to a
 * returning user whose Apple ID already burned its intro offer, and then charge
 * them immediately. This is the only signal that reflects the actual person.
 *
 * **iOS only.** Android always answers UNKNOWN (per the SDK's own docs), so
 * callers must NOT apply the fail-closed rule there — see useTrialEligibility.
 * Returns {} on web/Android/error.
 *
 * Fails CLOSED: only INTRO_ELIGIBILITY_STATUS_ELIGIBLE maps to true.
 * INELIGIBLE / NO_INTRO_OFFER_EXISTS / UNKNOWN all map to false, which is
 * RevenueCat's own guidance — "the best course of action on unknown status is
 * to display the non-intro pricing, to not create a misleading situation."
 */
export const checkIntroEligibility = async (
  productIds: string[],
): Promise<Record<string, boolean>> => {
  if (!isNative || !Purchases || Capacitor.getPlatform() !== 'ios') return {};
  if (productIds.length === 0) return {};

  try {
    // The enum is a VALUE, so it can't be imported at module scope without
    // pulling native-only code into the web bundle — read it off the same
    // lazily-imported module (dynamic imports are cached, so this is cheap).
    const { INTRO_ELIGIBILITY_STATUS } = await import('@revenuecat/purchases-capacitor');

    const result = await Purchases.checkTrialOrIntroductoryPriceEligibility({
      productIdentifiers: productIds,
    });

    const map: Record<string, boolean> = {};
    for (const productId of productIds) {
      // NOTE: `status` is a NUMERIC enum. Comparing it to a string literal
      // (e.g. 'ELIGIBLE') is always false and would silently hide the trial
      // from everyone.
      map[productId] =
        result?.[productId]?.status === INTRO_ELIGIBILITY_STATUS.INTRO_ELIGIBILITY_STATUS_ELIGIBLE;
    }
    return map;
  } catch (err) {
    logError(err, { context: 'purchases_check_intro_eligibility' });
    return {};
  }
};

/**
 * Restore previous purchases. Required by Apple App Store guidelines.
 * Returns the entitlements after restoration.
 */
export const restorePurchases = async (): Promise<PurchaseResult> => {
  if (!isNative || !Purchases) {
    return { success: false, entitlements: [], error: 'Purchases not available' };
  }

  try {
    const { customerInfo } = await Purchases.restorePurchases();
    const entitlements = activeIdsOf(customerInfo);
    const active = (customerInfo as {
      entitlements?: { active?: Record<string, { isActive?: boolean; willRenew?: boolean; store?: string }> };
    })?.entitlements?.active ?? {};
    const activeEntries = Object.entries(active).filter(([, ent]) => ent.isActive);
    const activeStores = [...new Set(
      activeEntries.map(([, ent]) => String(ent.store ?? 'UNKNOWN_STORE').toUpperCase()),
    )];
    const storeBilled = activeEntries.filter(([, ent]) =>
      DEVICE_STORES.includes(String(ent.store ?? '').toUpperCase()),
    );
    // Surface whether the restored membership actually renews: restoring a
    // cancelled-but-active sub gives access until expiry, NOT a resubscribe,
    // and callers must not imply otherwise. Judge the store-billed
    // entitlements when any exist — a dead web sub riding along in
    // CustomerInfo must not veto a genuinely-renewing restored one. Only an
    // explicit false on every relevant entitlement counts — unknown renewal
    // state reads as renewing.
    const relevant = storeBilled.length > 0 ? storeBilled : activeEntries;
    const willRenew = relevant.length > 0
      ? relevant.some(([, ent]) => ent.willRenew !== false)
      : undefined;
    return {
      success: true,
      entitlements,
      willRenew,
      storeBilledEntitlements: storeBilled.map(([id]) => id),
      activeStores,
    };
  } catch (err: any) {
    logError(err, { context: 'purchases_restore' });
    return { success: false, entitlements: [], error: err?.message ?? 'Restore failed' };
  }
};

/**
 * An active entitlement on the current RevenueCat app user, with the store
 * that unlocked it. `store` is RC's own enum — 'APP_STORE', 'PLAY_STORE',
 * 'STRIPE', 'PROMOTIONAL', … — which is what lets callers distinguish a
 * store purchase (restorable via the native Restore flow) from a granted or
 * web entitlement (nothing to restore on device).
 */
export interface ActiveEntitlement {
  id: string;
  store: string;
  /** RC's period type — 'TRIAL', 'INTRO' or 'NORMAL'; undefined if the SDK omits it. */
  periodType?: string;
}

/**
 * Active entitlements for the CURRENT RevenueCat app user, straight from
 * the SDK (no server round-trip). Waits briefly for the SDK to finish
 * initializing (it races React effect order on cold start). Returns [] on
 * web, on error, or when the SDK never initializes — callers treat that as
 * "no entitlements", never as a hard failure.
 *
 * Used by the pre-login stranded-payer check: an ANONYMOUS user with an
 * active entitlement bought at the pre-login paywall but never created an
 * account — the store is billing them, so route them back to sign-up.
 */
export const getActiveEntitlements = async (): Promise<ActiveEntitlement[]> => {
  if (!isNative) return [];

  // The SDK is configured from a SubscriptionContext effect that can run
  // AFTER an early caller's effect (React runs child effects first) — and on
  // a cold start over a weak connection the Remote Config fetch gating that
  // init can itself take several seconds. Wait generously: the stranded-payer
  // check runs exactly once, and giving up early costs a paying user their
  // resume (first seen on the 2026-07-17 TestFlight run).
  for (let tries = 0; !initialized && tries < 30; tries++) {
    await new Promise((r) => setTimeout(r, 500));
  }
  if (!initialized || !Purchases) return [];

  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    return Object.entries(customerInfo?.entitlements?.active ?? {})
      .filter(([, ent]) => (ent as { isActive?: boolean }).isActive)
      .map(([id, ent]) => ({
        id,
        store: (ent as { store?: string }).store ?? 'UNKNOWN_STORE',
        periodType: (ent as { periodType?: string }).periodType,
      }));
  } catch (err) {
    logError(err, { context: 'purchases_get_active_entitlements' });
    return [];
  }
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Whether native in-app purchases are available on this platform */
export const isNativePurchasesAvailable = (): boolean => isNative && initialized;
